// Auto-advances the spotlight collection-links image on mobile, where there is no hover.
//
// Horizon's collection-links spotlight layout already renders its images as a slideshow
// that is two-way synced to the text list (assets/collection-links.js #handleSlideshowSelect),
// but nothing signals that on mobile, and nothing moves without a swipe. This attaches from
// outside via the component's public select(index) method.
//
// It also builds the slide indicator dots, because sections/collection-links.liquid calls
// `render 'slideshow'` without the `controls` param, so Horizon renders none for this section.
//
// Homepage cleaner update 2026-08-17: reduce the oversized spotlight heading scale and image footprint.

(() => {
  const MOBILE = '(max-width: 749px)';
  const INTERVAL = 4000;
  const CLEANER_STYLE_ID = 'elevate-homepage-collection-links-cleaner';

  const injectCleanerStyles = () => {
    if (document.getElementById(CLEANER_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = CLEANER_STYLE_ID;
    style.textContent = `
      collection-links-component[layout='spotlight'] {
        grid-gap: clamp(0.75rem, 1.4vw, 1.75rem);
      }

      collection-links-component[layout='spotlight'] .collection-links__container {
        gap: clamp(0.35rem, 0.8vw, 0.9rem);
        max-width: 42rem;
      }

      collection-links-component[layout='spotlight'] .collection-links__link {
        --min-font-size: 1.45rem;
        --max-font-size: 2.7rem;
        font-size: clamp(var(--min-font-size), 2.25vw, var(--max-font-size));
        line-height: 1.08;
        font-weight: 650;
      }

      collection-links-component[layout='spotlight'] .collection-links__count {
        font-size: 0.38em;
        vertical-align: super;
      }

      collection-links-component[layout='spotlight'] .collection-links__images {
        justify-self: start;
        width: min(100%, 34rem);
      }

      collection-links-component[layout='spotlight'] .collection-links__images image-block {
        --image-height-large: clamp(18rem, 30vw, 28rem);
        --image-height-medium: clamp(16rem, 26vw, 24rem);
      }

      collection-links-component[layout='spotlight'] .collection-links__images image-block img,
      collection-links-component[layout='spotlight'] .collection-links__images image-block svg {
        border-radius: 8px;
      }

      @media screen and (min-width: 990px) {
        collection-links-component[layout='spotlight'] {
          grid-template-columns: minmax(18rem, 42rem) minmax(20rem, 34rem);
          justify-content: center;
          max-width: 78rem;
          margin-inline: auto;
          column-gap: clamp(0.9rem, 1.5vw, 1.75rem);
        }

        collection-links-component[layout='spotlight'][reverse] {
          grid-template-columns: minmax(20rem, 34rem) minmax(18rem, 42rem);
        }
      }

      @media screen and (min-width: 1500px) {
        collection-links-component[layout='spotlight'] {
          grid-template-columns: minmax(18rem, 39rem) minmax(20rem, 33rem);
          max-width: 74rem;
          column-gap: 1.25rem;
        }
      }

      @media screen and (max-width: 749px) {
        collection-links-component[layout='spotlight'] {
          grid-gap: 1rem;
        }

        collection-links-component[layout='spotlight'] .collection-links__container {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 0.6rem 1rem;
          max-width: none;
        }

        collection-links-component[layout='spotlight'] .collection-links__link {
          --min-font-size: 1.05rem;
          --max-font-size: 1.5rem;
          font-size: clamp(var(--min-font-size), 5vw, var(--max-font-size));
          line-height: 1.12;
          opacity: 0.58;
        }

        collection-links-component[layout='spotlight'] .collection-links__link[aria-current='true'] {
          opacity: 1;
          border-left: 3px solid #e6002d;
          padding-left: 0.55rem;
        }

        collection-links-component[layout='spotlight'] .collection-links__images {
          max-height: 240px;
          width: 100%;
        }
      }
    `;
    document.head.appendChild(style);
  };

  class MobileCycler {
    constructor(el) {
      this.el = el;
      this.timer = null;
      this.index = 0;
      this.stopped = false;
      this.dots = null;

      this.mq = window.matchMedia(MOBILE);
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.observer = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.start() : this.stop(false)),
        { threshold: 0.35 }
      );
      this.observer.observe(el);

      for (const ev of ['pointerdown', 'touchstart', 'keydown']) {
        el.addEventListener(ev, () => this.stop(true), { passive: true });
      }

      this.mq.addEventListener('change', () => {
        if (this.mq.matches) {
          this.buildDots();
          this.start();
        } else {
          this.stop(false);
        }
      });
    }

    get links() {
      return this.el.querySelectorAll('.collection-links__container a');
    }

    get count() {
      return this.links.length;
    }

    buildDots() {
      const n = this.count;
      const images = this.el.querySelector('.collection-links__images');
      if (!n || !images || this.dots) return;

      this.dots = document.createElement('div');
      this.dots.className = 'custom-cl-dots';
      this.dots.setAttribute('role', 'tablist');
      this.dots.setAttribute('aria-label', 'Select a category');

      for (let i = 0; i < n; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'custom-cl-dots__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', this.links[i]?.textContent.trim() || `Slide ${i + 1}`);
        dot.addEventListener('click', () => {
          this.stop(true);
          this.index = i;
          this.el.select?.(i);
          this.syncDots();
        });
        this.dots.appendChild(dot);
      }
      images.insertAdjacentElement('afterend', this.dots);
      this.syncDots();
    }

    syncDots() {
      if (!this.dots) return;
      [...this.dots.children].forEach((d, i) =>
        d.setAttribute('aria-selected', String(i === this.index))
      );
    }

    start() {
      if (this.stopped || this.timer) return;
      if (!this.mq.matches || this.reduced.matches) return;
      this.timer = setInterval(() => this.tick(), INTERVAL);
    }

    stop(permanent) {
      if (permanent) this.stopped = true;
      clearInterval(this.timer);
      this.timer = null;
    }

    tick() {
      const n = this.count;
      if (!n) return this.stop(false);
      this.index = (this.index + 1) % n;
      this.el.select?.(this.index);
      this.syncDots();
    }
  }

  const init = () => {
    injectCleanerStyles();

    for (const el of document.querySelectorAll('collection-links-component')) {
      if (el.dataset.mobileCycler) continue;
      el.dataset.mobileCycler = '1';
      const c = new MobileCycler(el);
      if (c.mq.matches) c.buildDots();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init after theme-editor section re-renders.
  document.addEventListener('shopify:section:load', init);
})();
